/**
 * SmsSettingsPage — SMS beallitasok, sablonok, kuldes, tortenelem
 * Epic 67: SMS Gateway & Kétirányú Protokoll
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCreateSmsTemplate, useSendSms, useSmsHistory, useSmsTemplates } from '@/hooks/useSms';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

// Status colors
const STATUS_COLORS: Record<string, string> = {
  SENT: 'text-green-600',
  DELIVERED: 'text-green-700',
  PENDING: 'text-yellow-600',
  FAILED: 'text-red-600',
  REJECTED: 'text-red-700',
};

// Direction labels are now handled via t() in the component

export function SmsSettingsPage() {
  const { t, locale } = useTranslation();
  const LOCALE_MAP: Record<string, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE', uk: 'uk-UA' };
  const [tab, setTab] = useState<'send' | 'history' | 'templates'>('send');
  const { result: history, isLoading: historyLoading } = useSmsHistory();
  const { result: templates, isLoading: templatesLoading } = useSmsTemplates();
  const sendSms = useSendSms();
  const createTemplate = useCreateSmsTemplate();

  // Send form
  const [sendForm, setSendForm] = useState({ phoneNumber: '', message: '' });
  const handleSend = async () => {
    if (!sendForm.phoneNumber || !sendForm.message) return;
    await sendSms.mutateAsync({
      phoneNumber: sendForm.phoneNumber,
      message: sendForm.message,
    });
    setSendForm({ phoneNumber: '', message: '' });
  };

  // Template form
  const [tplForm, setTplForm] = useState({
    name: '',
    type: 'CUSTOM',
    body: '',
    variables: '',
    active: true,
  });
  const handleCreateTemplate = async () => {
    if (!tplForm.name || !tplForm.body) return;
    const vars = tplForm.variables
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
    await createTemplate.mutateAsync({
      name: tplForm.name,
      type: tplForm.type,
      body: tplForm.body,
      variables: vars,
      active: tplForm.active,
    });
    setTplForm({ name: '', type: 'CUSTOM', body: '', variables: '', active: true });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('sms.title')}</h1>

      {/* Tab navigation */}
      <div className="flex gap-2 mb-6">
        <Button variant={tab === 'send' ? 'default' : 'outline'} onClick={() => setTab('send')}>
          {t('settings.sms.sendTab')}
        </Button>
        <Button
          variant={tab === 'history' ? 'default' : 'outline'}
          onClick={() => setTab('history')}
        >
          {t('settings.sms.historyTab')}
        </Button>
        <Button
          variant={tab === 'templates' ? 'default' : 'outline'}
          onClick={() => setTab('templates')}
        >
          {t('settings.sms.templatesTab')}
        </Button>
      </div>

      {/* SEND TAB */}
      {tab === 'send' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('sms.sendTab')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Input
                placeholder={t('sms.phonePlaceholder')}
                value={sendForm.phoneNumber}
                onChange={e => setSendForm({ ...sendForm, phoneNumber: e.target.value })}
              />
              <textarea
                className="w-full border rounded px-3 py-2 bg-background min-h-[100px]"
                placeholder={t('sms.messagePlaceholder')}
                maxLength={480}
                value={sendForm.message}
                onChange={e => setSendForm({ ...sendForm, message: e.target.value })}
              />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                  {t('sms.charCount', { count: sendForm.message.length })}
                </span>
                <Button onClick={handleSend} disabled={sendSms.isPending}>
                  {sendSms.isPending ? t('sms.sending') : t('sms.send')}
                </Button>
              </div>
              {sendSms.isSuccess && (
                <p className="text-green-600 text-sm">{t('sms.sentSuccess')}</p>
              )}
              {sendSms.isError && (
                <p className="text-red-600 text-sm">
                  {t('common.error')}: {sendSms.error?.message ?? t('common.unknownError')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('sms.historyTitle', { count: history?.total ?? 0 })}</CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="text-center py-4 text-gray-500 dark:text-[#ccfbf1]">
                {t('common.loading')}
              </div>
            ) : !history?.logs.length ? (
              <p className="text-gray-500 dark:text-[#ccfbf1] text-center py-4">
                {t('sms.noRecords')}
              </p>
            ) : (
              <div className="divide-y">
                {history.logs.map(log => (
                  <div key={log.id} className="py-3 flex justify-between items-center">
                    <div>
                      <span className="font-medium">{log.phoneNumber}</span>
                      <span className="ml-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
                        {log.direction === 'OUTBOUND'
                          ? t('sms.directionOutbound')
                          : log.direction === 'INBOUND'
                            ? t('sms.directionInbound')
                            : log.direction}
                      </span>
                      <span className={`ml-2 text-sm ${STATUS_COLORS[log.status] ?? ''}`}>
                        {log.status}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 dark:text-[#ccfbf1] max-w-xs truncate">
                        {log.message}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-[#99f6e4]">
                        {new Date(log.createdAt).toLocaleString(LOCALE_MAP[locale] ?? 'hu-HU')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* TEMPLATES TAB */}
      {tab === 'templates' && (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('sms.newTemplate')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder={t('sms.templateName')}
                  value={tplForm.name}
                  onChange={e => setTplForm({ ...tplForm, name: e.target.value })}
                />
                <select
                  className="border rounded px-3 py-2 bg-background"
                  value={tplForm.type}
                  onChange={e => setTplForm({ ...tplForm, type: e.target.value })}
                >
                  <option value="CUSTOM">{t('sms.typeCustom')}</option>
                  <option value="BOOKING_CONFIRM">{t('sms.typeBookingConfirm')}</option>
                  <option value="RENTAL_EXPIRY">{t('sms.typeRentalExpiry')}</option>
                  <option value="SERVICE_READY">{t('sms.typeServiceReady')}</option>
                  <option value="RENTAL_REMINDER">{t('sms.typeRentalReminder')}</option>
                </select>
              </div>
              <textarea
                className="w-full border rounded px-3 py-2 bg-background min-h-[80px] mt-3"
                placeholder={t('sms.templateBody')}
                value={tplForm.body}
                onChange={e => setTplForm({ ...tplForm, body: e.target.value })}
              />
              <Input
                className="mt-3"
                placeholder={t('sms.templateVariables')}
                value={tplForm.variables}
                onChange={e => setTplForm({ ...tplForm, variables: e.target.value })}
              />
              <div className="flex justify-between items-center mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={tplForm.active}
                    onChange={e => setTplForm({ ...tplForm, active: e.target.checked })}
                  />
                  {t('sms.templateActive')}
                </label>
                <Button onClick={handleCreateTemplate} disabled={createTemplate.isPending}>
                  {createTemplate.isPending ? t('sms.templateSaving') : t('sms.templateSave')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('sms.templatesTitle', { count: templates?.total ?? 0 })}</CardTitle>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <div className="text-center py-4 text-gray-500 dark:text-[#ccfbf1]">
                  {t('common.loading')}
                </div>
              ) : !templates?.templates.length ? (
                <p className="text-gray-500 dark:text-[#ccfbf1] text-center py-4">
                  {t('sms.noTemplates')}
                </p>
              ) : (
                <div className="divide-y">
                  {templates.templates.map(tpl => (
                    <div key={tpl.id} className="py-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{tpl.name}</span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${tpl.active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 dark:bg-[#1f5f6b] text-gray-600 dark:text-[#ccfbf1]'}`}
                        >
                          {tpl.active ? t('sms.activeLabel') : t('sms.inactiveLabel')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-[#ccfbf1] mt-1">{tpl.body}</p>
                      {tpl.variables.length > 0 && (
                        <div className="mt-1">
                          {tpl.variables.map(v => (
                            <span
                              key={v}
                              className="text-xs bg-gray-100 dark:bg-[#1f5f6b] px-1.5 py-0.5 rounded mr-1"
                            >
                              {`{${v}}`}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
